import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowClockwise, Heartbeat, CheckCircle, XCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin → Health tab.
 * Lists recent health reports (quick + deep) and lets the admin trigger an
 * on-demand check. State is fully local — fetched on mount and after each
 * triggered run.
 */
const HealthTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/health-reports?limit=20`, { withCredentials: true });
      setReports(res.data.reports);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const runCheck = async (deep = false) => {
    setRunning(true);
    try {
      await axios.post(`${API}/api/admin/health-check/run?deep=${deep}`, {}, { withCredentials: true });
      toast.success(deep ? 'Deep test complete' : 'Quick check complete');
      fetchReports();
    } catch {
      toast.error('Health check failed');
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="health-tab-content">
      {/* Actions */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => runCheck(false)}
          disabled={running}
          className="px-4 py-2 rounded-xl bg-cyan-500/20 text-cyan-400 font-medium text-sm hover:bg-cyan-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="run-quick-check-btn"
        >
          <ArrowClockwise className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
          Quick Check
        </button>
        <button
          onClick={() => runCheck(true)}
          disabled={running}
          className="px-4 py-2 rounded-xl bg-indigo-500/20 text-indigo-400 font-medium text-sm hover:bg-indigo-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="run-deep-test-btn"
        >
          <Heartbeat className={`w-4 h-4 ${running ? 'animate-pulse' : ''}`} />
          Deep Test Suite
        </button>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Auto: Quick checks every 30 min, deep tests every 6 hours
      </p>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading reports...</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12">
          <Heartbeat className="w-12 h-12 text-slate-700 mx-auto mb-2" />
          <p className="text-slate-500">No health reports yet. Run a check to start monitoring.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report, i) => (
            <div key={report._id || report.id || report.timestamp || i} className={`rounded-xl border p-4 ${
              report.all_passed
                ? 'bg-green-500/5 border-green-500/20'
                : 'bg-red-500/5 border-red-500/20'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                    report.all_passed ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                  <span className="text-white font-medium text-sm capitalize">{report.type} Check</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    report.all_passed
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {report.passed}/{report.total} passed
                  </span>
                </div>
                <span className="text-xs text-slate-500">
                  {new Date(report.timestamp).toLocaleString()}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {report.checks.map((check, j) => (
                  <div key={`${report._id || report.timestamp || i}-${check.name || j}`} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                    check.passed
                      ? 'bg-slate-800/50 text-slate-300'
                      : 'bg-red-500/10 text-red-300'
                  }`}>
                    {check.passed ? (
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                    )}
                    <span className="truncate">{check.endpoint || check.check || 'Check'}</span>
                    {check.latency_ms && (
                      <span className="ml-auto text-slate-600 flex-shrink-0">{check.latency_ms}ms</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HealthTab;
